import React, {useState, useContext, useEffect, createRef} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import { NavLink } from "react-router-dom";
import _ from "underscore";
import __ from "lodash";
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import {Badge, ClickAwayListener, Grow, IconButton, Menu, MenuItem, MenuList, Paper, Popper, Tooltip} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import Button from "@material-ui/core/Button";

import SystemContext from "../SystemContext";
import PasswordChange from "./PasswordChange";
import UserProfile from "./UserProfile";
import AlertDialog from "./AlertDialog";
import ConfirmDialog from "./ConfirmDialog";
import AuditTrail from "../modules/Audit/AuditTrail";
import LoadingProgress from "./LoadingProgress";
import AuthenticationButton from "../controls/AuthenticationButton";
import HoverMenu from "../controls/HoverMenu";
import NestedMenu from "../controls/NestedMenu";
import SessionTimeout from "../auth/SessionTimeout";
import Ticker from './Ticker';
import SendEmailDialog from "./SendEmailDialog";
import { useQueryClient } from 'react-query';
import { useAddBackgroundReport, useBackgroundReports, useDeleteBackgroundReport } from '../services/system';
import { v4 as uuidv4 } from 'uuid';
import Icons from '../icons';
import moment from 'moment';

const AccessControl = require("../AccessControl");
const version = `Version: ${process.env.REACT_APP_VERSION}`;

const BackgroundReports = (props) => {
  const SystemData = props.SystemData;
  const queryClient = useQueryClient();

  const backgroundReportsTimer = createRef(null);

  useEffect(() => {
    if (!backgroundReportsTimer.current) {
      backgroundReportsTimer.current = setInterval(() => {
        queryClient.invalidateQueries(['BackgroundReports']);
      }, 30000);
    }
    return () => {
      if (backgroundReportsTimer.current) {
        clearInterval(backgroundReportsTimer.current);
        backgroundReportsTimer.current = null;
      }
    }
  })

  const backgroundReports = useBackgroundReports({queryClient, extraKeys:[], useCache: false});
  const handleDeleteBackgroundReport = useDeleteBackgroundReport();

  const reportCount = backgroundReports.data ? __.filter(backgroundReports.data, (report) => {return ['COMPLETED', 'FAILED'].indexOf(report.status) !== -1}).length : 0;

  const ReportsBadge = withStyles(theme => ({
    badge: {
      height: 15,
      minWidth: 15,
      borderRadius: 7
    },
  }))(Badge);

  const viewBackgroundReports = (SystemData) => {

    //TODO: Replace with data from Settings or Combo's
    const reportNames = {
      'anticipatedCosts': 'Anticipated Costs Report',
      'budgetCosts': 'Finance Budget Report',
    }

    const downloadReport = (report) => {
      const fileName = report.reportFileName;
      let a = document.createElement('a');
      a.href = report.reportUrl;
      a.download = fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(async () => {
        document.body.removeChild(a);
        await new Promise((resolve, reject) => {handleDeleteBackgroundReport.mutate({oldRecord: report, resolve, reject, extraKeys: []})});
        queryClient.invalidateQueries(['BackgroundReports']);
      }, 0);
      SystemData.closeConfirm();
    }
    let reportContent = (
      <div style={{width: 450}}>
        {(backgroundReports?.data||[]).length ?
          <div>
            {(backgroundReports?.data||[]).map((report) => {
              const {reportType, status, backgroundReportUuid, requestedAt} = report;
              if (status === 'COMPLETED') {
                return (
                  <div key={backgroundReportUuid} style={{width: 430}}>
                    <div style={{display: 'inline-block', width: 200}}>{reportNames[reportType] || reportType}</div>
                    <div style={{display: 'inline-block', width: 100}}>{moment(requestedAt).format('DD MMM HH:mm')}</div>
                    <div style={{display: 'inline-block', width: 100}}>{status}</div>
                    <div style={{display: 'inline-block', width: 30}}><IconButton onClick={() => downloadReport(report)}><Icons>file_download</Icons></IconButton></div>
                  </div>
                )
              }
              return (
                <div key={backgroundReportUuid} style={{width: 430}}>
                  <div style={{display: 'inline-block', width: 200}}>{reportNames[reportType] || reportType}</div>
                    <div style={{display: 'inline-block', width: 100}}>{moment(requestedAt).format('DD MMM HH:mm')}</div>
                    <div style={{display: 'inline-block', width: 100, backgroundColor: status === 'FAILED' ? 'red' : 'inherit', color: status === 'FAILED' ? 'white' : 'inherit'}}>{status}</div>
                    <div style={{display: 'inline-block', width: 30}}></div>
                </div>
              );
            })}
          </div>
        :
          <div style={{textAlign: 'center', width: 430, paddingTop: 30, paddingBottom: 30}}>No Reports Requested</div>
        }
      </div>
    );

    SystemData.confirm(
      `Background Reports`,
      reportContent,
      [{label: "Close", value: "close"}],
      () => {reportContent = null},
      {dialogOptions: {style:{zIndex: 5000}, width: "lg", maxWidth: 800, PaperProps:{style: {backgroundColor: "rgb(240, 240, 240)"}}}}
    );
  }

  return (
    <Tooltip title="Background Reports">
      <ReportsBadge badgeContent={reportCount} color="primary">
        <IconButton style={{padding: 0}} onClick={() => viewBackgroundReports(SystemData)}>
          <Icons>dynamic_feed</Icons>
        </IconButton>
      </ReportsBadge>
    </Tooltip>
  )
}

const PageHeader = (props) => {
  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;

  const handleAddBackgroundReport = useAddBackgroundReport();

  const { logout, isAuthenticated } = useAuth0();
  const userInfo = AccessControl.getUserInfo();
  let userNameWidth = userInfo?.userClass !== "OPERATOR" ? 400 : 200;
  let menuWidthRestriction = userInfo?.userClass !== "OPERATOR" ? 700 : 500;

  let theme = useTheme();
  const values = {
    xs: 0,
    sm: 600,
    lsm: 720,
    md: 960,
    mlg: 1120,
    lg: 1280,
    lgr: 1750,
    xl: 1920,
    xxl: 2400
  };

  theme.breakpoints = {...theme.breakpoints,
    keys: ['xs', 'sm', 'lsm', 'md', 'mlg', 'lg', 'lgr', 'xl', 'xxl'],
    up: key => `@media (min-width:${values[key]}px)`,
  };

  const classes = makeStyles({
    pageMargins: {
      marginLeft: "40px",
      marginRight: "40px",
      [theme.breakpoints.down('md')]: {
        marginLeft: "5px",
        marginRight: "5px"
      }
    },
    menuBar: {
      display: "block",
      marginBlockStart: "0px",
      marginBlockEnd: "0px",
      [theme.breakpoints.down('md')]: {
        display:"none"
      }
    },
    mobileMenu: {
      display: "none",
      [theme.breakpoints.down('md')]: {
        display:"inline-block"
      }
    },
    sectionWide: {
      float: "right",
      width: "75%",
      height: "100%",
      padding: 0,
    },
    sectionNarrow: {
      float: "right",
      width: "25%",
      height: "100%",
      padding: 0,
    },
    avatar: {
      backgroundColor: theme.palette.secondary.main,
    },
    userName: {
      color: "#000000",
      display:"block",
      padding: 0,
      minHeight: 0,
      minWidth: 0,
      fontWeight: 600,
      fontFamily:"Ubuntu,sans-serif",
      fontSize: 'small',
      textDecoration: "none",
      textTransform: 'none'
    },
    pageName: {
      backgroundColor: theme.palette.primary.main,
      padding:"10px",
      position: "relative",
      overflow: "visible",
      width: "auto",
      float: "right",
      whiteSpace: "nowrap",
      zIndex: 2,
      "& h2": {
        color: theme.palette.primary.contrastText,
        padding: 0,
        margin:"2px 0px",
        fontSize:20,
        fontWeight:500,
        fontFamily:"Ubuntu, sans-serif",
        fontVariantCaps: "small-caps",
      }
    }
  })();

  const StyledMenu = withStyles({
    paper: {
      border: '1px solid #d3d4d5',
    },
  })(props => (
    <Menu
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      {...props}
    />
  ));

  const StyledMenuItem = withStyles(theme => ({
    root: {
      '&:focus': {
        backgroundColor: theme.palette.primary.main,
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
          color: theme.palette.common.white,
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.2)'
      },
      '&.Mui-selected:hover': {
        backgroundColor: 'rgba(0,0,0,0.5)'
      },
    },
  }))(MenuItem);

  const [menuItems, setMenuItems] = useState(SystemData.menuItems);

  useEffect(() => {
    setMenuItems(SystemData.menuItems);
  }, [SystemData.menuItems]);

  // const menuItems = SystemData.menuItems;
  const [openUserMenu, setOpenUserMenu] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpenUserMenu((prevOpen) => !prevOpen);
  };

  const handleUserMenuClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    if (event.currentTarget && event.currentTarget.outerText === "Change Password") {
      setPasswordChangeState({ ...passwordChangeState, open: true });
    }

    if (event.currentTarget && event.currentTarget.outerText === "My Account") {
      setUserProfile({ ...userProfile, open: true });
    }

    if (event.currentTarget && event.currentTarget.outerText === "Reset Ticker") {
      eventBus.trigger("Ticker.reset");
    }

    setOpenUserMenu(false);
  };

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenUserMenu(false);
    }
  }

  const MenuBar = (props) => {

    return (
      <ul className={classes.menuBar+" nav navbar-nav"} style={{listStyle:"none", width:"100%", ...props.style}}>
      {
        SystemData.menuItems.map((menuItem) => {
          if ((menuItem.roles.indexOf("ANY") >= 0 || AccessControl.userHasAccess(props.loggedInUser, menuItem.roles)) && menuItem.parentId === "") {
            const items = _.where(menuItems, {parentId: menuItem.id}).length === 0 ? _.where(menuItems, {id: menuItem.id}) : _.filter(_.where(menuItems, {parentId: menuItem.id}), (option)=> {
              return AccessControl.userHasAccess(props.loggedInUser, option.roles);
            });
            let nestedItems = [];
            items.forEach(item => {
                const nested = _.filter(_.where(menuItems, {parentId: item.id}), (option)=> {  return AccessControl.userHasAccess(props.loggedInUser, option.roles); });
              if (nested && Array.isArray(nested))
                nestedItems = [...nestedItems, ...nested];
            });
            return (
              <li key={menuItem.id} id={"menu-item-"+menuItem.id} style={{float:"left", display:"block"}} >
                <HoverMenu
                  menuName={ menuItem.name }
                  menuItems={ _.where(menuItems, {parentId: menuItem.id}).length === 0 ? _.where(menuItems, {id: menuItem.id}) : _.filter(_.where(menuItems, {parentId: menuItem.id}), (option)=> {
                    return AccessControl.userHasAccess(props.loggedInUser, option.roles);
                  })}
                  nestedItems={nestedItems}
                ></HoverMenu>
              </li>
            );
          } else return null;
        })
      }
      </ul>
    )
  };

  const MobileMenu = (props) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleMenuClick = event => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    return (
      <div className={classes.mobileMenu} style={{float:"left", width:"auto", padding:"20px 0px 0px 10px", fontSize:20, ...props.style}}>
        <div style={{backgroundColor:"#004288", color: "#ffffff"}}>
          <IconButton
            color="inherit"
            aria-label="open menu"
            aria-haspopup="true"
            onClick={handleMenuClick}
  //          className={clsx(classes.menuButton, open && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
          <StyledMenu
            id="customized-menu"
            key="customized-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {
              _.filter(menuItems, {parentId: ""}).map((menuItem) => {
                if (AccessControl.userHasAccess(props.loggedInUser, menuItem.roles)) {
                  const children=_.filter(_.where(menuItems, {parentId: menuItem.id}), (option)=> {
                    return AccessControl.userHasAccess(props.loggedInUser, option.roles);
                  });
                  if (Array.isArray(children) && children.length > 1) {
                    return (
                      <NestedMenu id={menuItem.id} key={menuItem.id} selected={false} label={menuItem.name} parentMenuOpen={true}>
                        {children.map(childItem => (
                          <StyledMenuItem key={childItem.id} selected={false}>
                            <NavLink to={childItem.path} isActive={()=>{return false}} activeStyle={{color: "#ffffff"}} style={{color: "#1b4481", textDecorationLine: "none"}}>{childItem.name}</NavLink>
                          </StyledMenuItem>
                        ))}
                      </NestedMenu>
                    );
                  }
                  if (!Array.isArray(children) || children.length < 1) {
                    return null;
                  }
                  return (
                    <StyledMenuItem key={menuItem.id} selected={false}>
                      <NavLink to={children[0].path} isActive={()=>{return false}} activeStyle={{color: "#ffffff"}} style={{color: "#1b4481", textDecorationLine: "none"}}>{menuItem.name}</NavLink>
                    </StyledMenuItem>
                  )
                } else return (null)
              })
            }
          </StyledMenu>
        </div>
      </div>
    )
  };

  const renderUserName = () => {
    const userInfo = AccessControl.getUserInfo();
    if (userInfo && "firstName" in userInfo && "lastName" in userInfo) {
      if ("companyName" in userInfo && userInfo.userClass !== "OPERATOR")
        return (`${userInfo.firstName} ${userInfo.lastName} (${userInfo.companyName})`);
      return (userInfo.firstName + " " + userInfo.lastName);
    }
    else
      return loggedInUser.name;
  }

  const [passwordChangeState, setPasswordChangeState] = useState({
    open: false
  });

  const [userProfile, setUserProfile] = useState({
    open: false
  });

  const [state, setState] = useState( {
    openAlert: false,
    alertTitle: "",
    alertMessage: "",
    alertOptions: null
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    buttons: [{icon: "check_circle", value: true, label: "Yes"}, {icon: "cancel", value: false, label: "No"}],
    onChange: null,
    preventClose: false,
    options: {}
  });

  const [sendEmailDialog, setSendEmailDialog] = useState({
    open: false,
  });

  const [auditTrail, setAuditTrail] = useState({
    open: false,
    objectUuid: null,
    title: "Audit Trail",
    onChange: ()=>{setAuditTrail({...auditTrail, open: false, objectUuid: null, title: "Audit Trail"})}
  });

  const [showLoadingProgress, setShowLoadingProgress] = useState({show: false, message: null});
  const toggleLoadingProgress = (status, message = null) => {
    if (status !== undefined) {
      setShowLoadingProgress({show: status, message});
    } else {
      setShowLoadingProgress({show: !showLoadingProgress.show, message: null});
    }
  }
  SystemData['toggleLoadingProgress'] = toggleLoadingProgress;


  function openAlert (alertTitle, alertMessage, alertOptions=null) {
    const openAlert = true;
    setState({...state, openAlert, alertTitle, alertMessage, alertOptions});
  }
  SystemData['openAlert'] = openAlert;
  SystemData['alert'] = openAlert;

  function confirm (title, message, buttons=[{icon: "check_circle", value: true, label: "Yes"}, {icon: "cancel", value: false, label: "No"}], onChange, options) {
    const open = true;
    setConfirmDialog({...confirmDialog, open, title, message, buttons, onChange, options});
  }
  SystemData['confirm'] = confirm;
  SystemData['closeConfirm'] = () => setConfirmDialog({...confirmDialog, open: false});

  function openAuditTrail (objectUuid, title) {
    const open = true;
    setAuditTrail({...auditTrail, objectUuid, title, open});
  }
  SystemData['openAuditTrail'] = openAuditTrail;

  function sendEmail (record, title, canEdit) {
    const open = true;
    setSendEmailDialog({...sendEmailDialog, open, title});
    SystemData.eventBus.trigger("SendEmailDialog.open", {record, title, canEdit});
  }
  SystemData['sendEmail'] = sendEmail;

  const handleLogout = () => {
    logout({returnTo: window.location.origin});
  }

  const requestBackgroundReport = async (reportType, reportParameters) => {
    const reportRecord = {
      backgroundReportUuid: uuidv4(),
      reportType,
      reportParameters,
      status: 'REQUESTED',
    }

    await new Promise((resolve, reject) => {
      handleAddBackgroundReport.mutate(
        {
          record: reportRecord,
          resolve,
          reject,
          extraKeys:[],
          postSuccess: () => {
            openAlert("Information", "Background Report has been queued for processing");
          },
          postError: (err) => {
            openAlert("Error", err?.errorMessage || "An unexpected error has occurred");
          }
        }
      );
    })
  }
  SystemData['requestBackgroundReport'] = requestBackgroundReport;

  const pageName = props.pageName;
  const loggedInUser = props.loggedInUser;

  return (
    <div className={classes.pageMargins +" clearfix"} style={{marginBottom:1, backgroundColor:process.env.REACT_APP_HEADER_INFO?'yellow':theme.palette.background.default}}>
      <MobileMenu activeItem={props.activeMenuItem} alert={openAlert} loggedInUser={loggedInUser} />
      <Tooltip title={version}>
        <div style={{float:"left", width:200, padding:"18px 10px 0px 10px", margin:0}}>
          <img style={{maxWidth:"250px", height:"53px"}} src={process.env.REACT_APP_COMPANY_LOGO ? process.env.REACT_APP_COMPANY_LOGO : "images/logo.png"} alt="logo" />
          <h2 style={{color:"#000000", margin:"0px 0px", fontSize:16, fontWeight:500, fontFamily:"Ubuntu, sans-serif"}}>&nbsp;{process.env.REACT_APP_HEADER_INFO}</h2>
        </div>
      </Tooltip>
      <div style={{float:"left", padding:0, margin:0, width:`calc(100% - ${menuWidthRestriction}px)`}}>
        <div style={{padding:"0px 0px 0 40px", height: 65}}>
          <Ticker loggedInUser={loggedInUser}/>
        {/* <div style={{padding:"2px"}}>
            <h2 style={{color:"#000000", margin:"2px 0px", fontSize:20, fontWeight:500, fontFamily:"Ubuntu, sans-serif"}}>&nbsp;{process.env.REACT_APP_HEADER_INFO}</h2>
          </div>
          <div style={{padding:"2px", backgroundColor: "#909090"}}>
            <h2 style={{color:"#000000", margin:"2px 0px", fontSize:20, fontWeight:500, fontFamily:"Ubuntu, sans-serif"}}>&nbsp;{process.env.REACT_APP_HEADER_INFO}</h2>
          </div>*/}
        </div>
        <div>
        <nav className="navbar-collapse bs-navbar-collapse collapse">
          <MenuBar activeItem={props.activeMenuItem} alert={openAlert} loggedInUser={loggedInUser}/>
        </nav>
        </div>
      </div>
      <div style={{float:"right", padding:"15px 5px 0 0", width: userNameWidth}}>
        <div className={classes.sectionNarrow}>
          <div style={{padding:"5px", marginRight: 0, overflow: "visible", width: "auto", float: "right", whiteSpace: "nowrap"}}>
            <AuthenticationButton />
          </div>
        </div>
        <div className={classes.sectionWide}>
          <div className={classes.pageName}>
            <h2>{pageName}</h2>
          </div>
        </div>
        <div style={{padding: "58px 0 0 0"}}>
          <div style={{float:"right", width: "auto", padding:0, marginRight:7, textAlign: "left"}}>
            <BackgroundReports SystemData={SystemData}/>
          </div>
          <div style={{float:"right", width: "auto", padding:0, marginRight:7, textAlign: "left"}}>
            <Button ref={anchorRef} onClick={handleToggle} className={classes.userName} >
              {renderUserName()}
            </Button>
            <Popper open={openUserMenu} anchorEl={anchorRef.current} role={undefined} transition disablePortal style={{ zIndex: 3100 }}>
              {({ TransitionProps, placement }) => (
                  <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: placement === 'bottom' ? 'length top' : 'length bottom' }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleUserMenuClose}>
                        <MenuList autoFocusItem={openUserMenu} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                          <MenuItem onClick={handleUserMenuClose}>Change Password</MenuItem>
                          <MenuItem onClick={handleUserMenuClose}>My Account</MenuItem>
                          <MenuItem onClick={handleUserMenuClose}>Reset Ticker</MenuItem>
                          <MenuItem onClick={() => logout({returnTo: window.location.origin})}>Logout</MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
              )}
            </Popper>
          </div>
        </div>
      </div>
      <PasswordChange
        open={passwordChangeState.open}
        user={loggedInUser}
        userName={loggedInUser.name}
        logout={logout}
        onChange={() => { setPasswordChangeState({ ...passwordChangeState, open: false }); }}
      ></PasswordChange>
      <UserProfile
        open={userProfile.open}
        user={loggedInUser}
        onChange={() => { setUserProfile({ ...userProfile, open: false }); }}
      ></UserProfile>
      <AlertDialog
        open={state.openAlert}
        alertTitle={state.alertTitle}
        alertMessage={state.alertMessage}
        options={state.alertOptions}
        onChange={() => { setState({ ...state, openAlert: false }); }}
      ></AlertDialog>
      {confirmDialog.open
        ?
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            buttons={confirmDialog.buttons}
            {...confirmDialog.options}
            onChange={(status) => {
              if (confirmDialog.onChange) {
                confirmDialog.onChange(status);
              }
              if (!confirmDialog.preventClose) {
                setConfirmDialog({ ...confirmDialog, open: false });
              }
            }}
          ></ConfirmDialog>
        :
          <></>
      }
      {sendEmailDialog.open
        ?
          <SendEmailDialog
            SystemData={SystemData}
            onChange={() => { setSendEmailDialog({ ...sendEmailDialog, open: false }); }}
          />
        :
          <></>
      }
      <AuditTrail
        {...auditTrail}
      />
      <LoadingProgress
        open={showLoadingProgress.show}
        message={showLoadingProgress.message}
      />
      <SessionTimeout
        isAuthenticated={isAuthenticated}
        logOut={handleLogout}
      />
    </div>
  )
};

export default PageHeader;
